import { useLocale, useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { Input } from '@multaqa/ui/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@multaqa/ui/components/ui/select'
import { ArrowDownIcon, ArrowUpIcon, XIcon } from 'lucide-react'
import { localizedText } from '@/lib/me'
import { SYSTEM_ROLE_KEYS } from '../roles/system-roles'
import { useWorkflowApproverMembers, useWorkflowApproverRoles } from './api'
import {
  applyPicker,
  delayIsValid,
  pickerValue,
  type WorkflowMode,
  type WorkflowStepDraft,
} from './workflow-levels'

/** Every built-in role but self-service employees: a level of "everyone" asks no one. */
const LEVEL_SYSTEM_ROLES = SYSTEM_ROLE_KEYS.filter((key) => key !== 'employee')

export function WorkflowLevelRow({
  companyId,
  step,
  index,
  count,
  mode,
  onChange,
  onRemove,
  onMove,
}: {
  companyId: string
  step: WorkflowStepDraft
  index: number
  count: number
  mode: WorkflowMode
  onChange: (step: WorkflowStepDraft) => void
  onRemove: () => void
  onMove: (offset: -1 | 1) => void
}) {
  const { t } = useTranslation('workflow')
  const { locale } = useLocale()
  const { data: members = [] } = useWorkflowApproverMembers(companyId)
  const { data: roles = [] } = useWorkflowApproverRoles(companyId)
  const number = index + 1
  const isLast = index === count - 1
  const label =
    mode === 'escalation' ? t('levels.levelLabel', { number }) : t('form.stepLabel', { number })
  const delayInvalid = !delayIsValid(step.escalate_after_hours)

  return (
    <li className="relative flex gap-3 pb-4 last:pb-0">
      {!isLast ? (
        <span aria-hidden="true" className="absolute start-3.5 top-8 bottom-0 w-px bg-border" />
      ) : null}
      <span
        aria-hidden="true"
        className="relative flex size-7 shrink-0 items-center justify-center rounded-full border border-border bg-background text-small font-medium"
      >
        {number}
      </span>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <span className="sr-only">{label}</span>
          <Select
            value={pickerValue(step)}
            onValueChange={(value) => onChange(applyPicker(step, value))}
          >
            <SelectTrigger
              className="min-w-0 flex-1"
              aria-label={
                mode === 'escalation'
                  ? t('levels.approverFor', { label })
                  : t('form.approverForStep', { number })
              }
            >
              <SelectValue placeholder={t('form.approverPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manager">{t('levels.directManager')}</SelectItem>
              <SelectSeparator />
              <SelectGroup>
                <SelectLabel>{t('levels.builtInRoles')}</SelectLabel>
                {LEVEL_SYSTEM_ROLES.map((key) => (
                  <SelectItem key={key} value={`system:${key}`}>
                    {t(`roles.systemRoles.${key}`, { ns: 'common' })}
                  </SelectItem>
                ))}
              </SelectGroup>
              {roles.length > 0 ? (
                <SelectGroup>
                  <SelectLabel>{t('levels.customRoles')}</SelectLabel>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={`role:${role.id}`}>
                      {localizedText(role.name, locale) || role.key}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ) : null}
              {members.length > 0 ? (
                <SelectGroup>
                  <SelectLabel>{t('levels.people')}</SelectLabel>
                  {members.map((member) => (
                    <SelectItem key={member.user.id} value={`user:${member.user.id}`}>
                      {member.user.name ?? member.user.email ?? member.user.id}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ) : null}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            aria-label={t('form.moveUp')}
          >
            <ArrowUpIcon className="size-3.5" aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={isLast}
            onClick={() => onMove(1)}
            aria-label={t('form.moveDown')}
          >
            <ArrowDownIcon className="size-3.5" aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={count === 1}
            onClick={onRemove}
            aria-label={t('form.removeStep')}
          >
            <XIcon className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
        {mode === 'escalation' ? (
          isLast ? (
            <p className="text-small text-muted-foreground">{t('levels.lastLevel')}</p>
          ) : (
            <div className="grid gap-1">
              <label
                className="flex flex-wrap items-center gap-2 text-small"
                htmlFor={`delay-${step.clientKey}`}
              >
                <span>{t('levels.moveUpAfter')}</span>
                <Input
                  id={`delay-${step.clientKey}`}
                  inputMode="numeric"
                  className="w-20"
                  value={step.escalate_after_hours}
                  aria-invalid={delayInvalid || undefined}
                  onChange={(event) =>
                    onChange({ ...step, escalate_after_hours: event.target.value })
                  }
                />
                <span>{t('levels.hoursIfNoDecision')}</span>
              </label>
              <p
                className={
                  delayInvalid ? 'text-small text-danger' : 'text-small text-muted-foreground'
                }
              >
                {delayInvalid ? t('levels.delayInvalid') : t('levels.delayHelper')}
              </p>
            </div>
          )
        ) : null}
      </div>
    </li>
  )
}
